import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { CATEGORY_ICONS } from "../lib/constants";
import { useT } from "../i18n/I18nContext";
import { MapPin } from "lucide-react";

// Default Leaflet marker icons need explicit URLs in bundlers
const DefaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function FitToMarkers({ items }) {
  const map = useMap();
  useEffect(() => {
    if (!items.length) return;
    const bounds = L.latLngBounds(items.map((it) => [it.lat, it.lng]));
    map.fitBounds(bounds.pad(0.25), { maxZoom: 13, animate: true });
  }, [items, map]);
  return null;
}

export default function MapPage() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/map/requests")
      .then(({ data }) => { if (!cancelled) setItems(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const initialCenter = useMemo(() => {
    if (items.length) return [items[0].lat, items[0].lng];
    return [41.9028, 12.4964]; // default: Rome
  }, [items]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 anim-fade-up" data-testid="map-page">
      <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">{t("map.overline")}</div>
      <h1 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: "Outfit" }}>{t("map.title")}</h1>
      <p className="text-mabw-soft mt-2">{t("map.sub")}</p>

      <div className="mt-8 card-mabw overflow-hidden" style={{ height: "640px" }} data-testid="map-container">
        {loading ? (
          <div className="h-full flex items-center justify-center text-mabw-soft" data-testid="map-loading">
            {t("common.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-mabw-soft p-10 text-center" data-testid="map-empty">
            <MapPin className="w-10 h-10 mb-3 text-mabw-secondary" />
            {t("map.no_results")}
          </div>
        ) : (
          <MapContainer
            center={initialCenter}
            zoom={6}
            scrollWheelZoom
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitToMarkers items={items} />
            {items.map((it) => {
              const Icon = CATEGORY_ICONS[it.category] || CATEGORY_ICONS.other;
              return (
                <Marker key={it.id} position={[it.lat, it.lng]} data-testid={`map-marker-${it.id}`}>
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="text-xs uppercase tracking-[0.18em] text-mabw-secondary font-semibold mb-1 flex items-center gap-1">
                        <Icon className="w-3.5 h-3.5" /> {t(`categories.${it.category}.label`)}
                      </div>
                      <div className="font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>{it.title}</div>
                      <div className="text-xs text-mabw-soft mt-1">{it.city}</div>
                      <Link
                        to={`/app/richieste/${it.id}`}
                        data-testid={`map-popup-open-${it.id}`}
                        className="inline-block mt-3 text-mabw-primary text-sm font-medium hover:underline"
                      >
                        {t("map.open_request")} →
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>
    </div>
  );
}
